import React, { useEffect, useRef, useState } from "react";

import AlertCircle from "@uui/AlertCircle";
import { map, reject, toLower, trim } from "lodash/fp";

import Typography from "@mui/material/Typography";

import type { ColorPickerProps } from "@app/analytics/components/ColorPicker";
import type { Tag as ITag } from "@app/common/types";
import useAnchorElement, {
  UseAnchorElement,
} from "@app/utils/hooks/useAnchorElement";

import { Row } from "@rui/Stack";

import SelectableTag from "./SelectableTag";
import {
  ActionButton,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverHeading,
  TagListContainer,
  TextField,
} from "./styles";
import TagInput from "./TagInput";

type ControlProps = Pick<UseAnchorElement, "onClick"> & { tag: ITag };

export interface EditCustomTagPopoverProps {
  /** All the user's tags (used to determine if tag is a duplicate) */
  allTags: ITag[];
  onSave: (tag: ITag) => void;
  renderControl: (props: ControlProps) => React.ReactNode;
  tag: ITag;
}

const EditCustomTagPopover = ({
  allTags,
  onSave,
  renderControl,
  tag,
}: EditCustomTagPopoverProps) => {
  const { open, onClose, onClick, anchorElement } = useAnchorElement();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [inputValue, setInputValue] = useState(tag.name);
  const [updatedTagColor, setUpdatedTagColor] = useState<ITag["color"] | null>(
    null
  );
  const [isDuplicate, setIsDuplicate] = useState(false);

  const otherTags = reject({ id: tag.id }, allTags);
  const existingTagNames = map((t) => trim(toLower(t.name)), otherTags);

  useEffect(() => {
    setInputValue(tag.name);
  }, [tag.name]);

  const focusInputElement = () => inputRef.current?.click();

  const handleClose = () => {
    onClose();
    if (inputValue !== tag.name) {
      setInputValue(tag.name);
    }
    setIsDuplicate(false);
    setUpdatedTagColor(null);
  };

  const handleOpenPopover: typeof onClick = (event) => {
    onClick(event);
    // short timeout to give time for the element to appear before trying to focus it
    setTimeout(focusInputElement, 100);
  };

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const updatedValue = event.target.value;
    setInputValue(updatedValue);
    setIsDuplicate(existingTagNames.includes(trim(toLower(updatedValue))));
  };

  const handleColorChange: ColorPickerProps["onChange"] = (color) => {
    setUpdatedTagColor(color.backgroundColor);
  };

  const handleSave = () => {
    onSave({ ...tag, name: inputValue, color: updatedTagColor ?? tag.color });
    onClose();
  };

  return (
    <>
      {renderControl({ onClick: handleOpenPopover, tag })}
      <Popover open={open} onClose={handleClose} anchorEl={anchorElement}>
        <PopoverContent spacing={1}>
          <PopoverHeader sx={{ borderBottom: "none" }}>
            <PopoverHeading>Edit Custom Tag</PopoverHeading>
          </PopoverHeader>
          <TextField autoFocus>
            <TagInput
              fullWidth
              onChange={handleInputChange}
              ref={inputRef}
              value={inputValue}
            />
          </TextField>
          {isDuplicate ? (
            <Row sx={{ pl: 0.5 }} spacing={0.5} variant="left">
              <AlertCircle sx={{ fontSize: "12px", color: "error.main" }} />
              <Typography variant="labelReg" sx={{ color: "error.main" }}>
                This tag already exists
              </Typography>
            </Row>
          ) : (
            <TagListContainer>
              <SelectableTag
                color={updatedTagColor ?? tag.color}
                name={inputValue || tag.name}
                onColorChange={handleColorChange}
                sx={{ pb: 0 }}
              />
            </TagListContainer>
          )}
          <Row spacing={1}>
            <ActionButton onClick={handleClose}>Cancel</ActionButton>
            <ActionButton
              disabled={!inputValue || isDuplicate}
              onClick={handleSave}
              variant="contained"
            >
              Save
            </ActionButton>
          </Row>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default EditCustomTagPopover;
